import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
    Computer, Cloud, Users, Cpu, Network, Globe, Grid, MoreHorizontal, Edit, Save, X
} from 'lucide-react';
import { Base_Url } from '../config';

const ServicesEditPage = () => {
    const navigate = useNavigate();
    const [pages, setPages] = useState([]);
    const [editingPage, setEditingPage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const iconMap = {
        'Computer': Computer,
        'Cloud': Cloud,
        'Users': Users,
        'Cpu': Cpu,
        'Network': Network,
        'Globe': Globe,
        'Grid': Grid,
        'MoreHorizontal': MoreHorizontal
    };

    useEffect(() => {
        const fetchPages = async () => {
            try {
                const response = await axios.get(`${Base_Url}/pages`);
                
                if (Array.isArray(response.data)) {
                    setPages(response.data);
                } else if (response.data && response.data.length) {
                    setPages(response.data);
                } else {
                    setPages([]);
                }

                setLoading(false);
            } catch (error) {
                console.error('Error fetching pages:', error);
                setError(error);
                setLoading(false);
            }
        };

        fetchPages();
    }, []);

    const handleEditPage = (page) => {
        setEditingPage({...page});
    };

    const handleSavePage = async () => {
        if (!editingPage) return;

        try {
            const response = await axios.put(`${Base_Url}/pages/${editingPage.id}`, {
                // Only send cardsinfo for update
                cardsinfo: editingPage.cardsinfo
            });
            
            // Update the pages list with the updated page
            setPages(pages.map(page => 
                page.id === editingPage.id ? {...page, cardsinfo: response.data.cardsinfo} : page
            ));

            // Exit edit mode
            setEditingPage(null);
        } catch (error) {
            console.error('Error saving page:', error);
            alert('Failed to save changes');
        }
    };

    const handleCancelEdit = () => {
        setEditingPage(null);
    };

    const handleCardInfoChange = (e, field) => {
        setEditingPage({
            ...editingPage,
            cardsinfo: {
                ...editingPage.cardsinfo,
                [field]: e.target.value
            }
        });
    };

    const handleItemsChange = (itemIndex, field, value) => {
        const updatedItems = [...editingPage.cardsinfo.items];
        updatedItems[itemIndex] = {
            ...updatedItems[itemIndex],
            [field]: value
        };

        setEditingPage({
            ...editingPage,
            cardsinfo: {
                ...editingPage.cardsinfo,
                items: updatedItems
            }
        });
    };

    const addNewItem = () => {
        setEditingPage({
            ...editingPage,
            cardsinfo: {
                ...editingPage.cardsinfo,
                items: [
                    ...(editingPage.cardsinfo.items || []),
                    { name: '', link: '' }
                ]
            }
        });
    };

    const removeItem = (indexToRemove) => {
        setEditingPage({
            ...editingPage,
            cardsinfo: {
                ...editingPage.cardsinfo,
                items: editingPage.cardsinfo.items.filter((_, index) => index !== indexToRemove)
            }
        });
    };

    // Render loading state
    if (loading) {
        return (
            <div className="text-center p-5">
                <h2>Loading Services...</h2>
                <p>Please wait while services are being loaded.</p>
            </div>
        );
    }

    // Render error state
    if (error) {
        return (
            <div className="alert alert-danger text-center p-5">
                <h2>Error Loading Services</h2>
                <p>Unable to fetch services. Please try again later.</p>
                <p>Error Details: {error.message}</p>
            </div>
        );
    }

    return (
        <div className="services-edit-section">
            <div className="container">
                <h2 className="page-title">
                    Edit <span className="highlight-title">Services</span>
                </h2>

                <div className="row g-4">
                    {pages.map((page) => (
                        <div key={page.id} className="col-md-6 col-lg-4">
                            <div className="service-edit-card">
                                {editingPage && editingPage.id === page.id ? (
                                    // Edit Mode
                                    <div className="card-edit-mode">
                                        <div className="form-group mb-3">
                                            <label>Title</label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                value={editingPage.cardsinfo.title || ''}
                                                onChange={(e) => handleCardInfoChange(e, 'title')}
                                            />
                                        </div>
                                        <div className="form-group mb-3">
                                            <label>Path</label>
                                            <input 
                                                type="text" 
                                                className="form-control" 
                                                value={editingPage.cardsinfo.path || ''}
                                                onChange={(e) => handleCardInfoChange(e, 'path')}
                                            />
                                        </div>
                                        <div className="form-group mb-3">
                                            <label>Icon</label>
                                            <select 
                                                className="form-control"
                                                value={editingPage.cardsinfo.icon || ''}
                                                onChange={(e) => handleCardInfoChange(e, 'icon')}
                                            >
                                                {Object.keys(iconMap).map(iconName => (
                                                    <option key={iconName} value={iconName}>
                                                        {iconName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Items Editing */}
                                        <div className="form-group mb-3">
                                            <label>Items</label>
                                            {editingPage.cardsinfo.items && editingPage.cardsinfo.items.map((item, index) => (
                                                <div key={index} className="d-flex mb-2">
                                                    <input 
                                                        type="text"
                                                        className="form-control me-2"
                                                        placeholder="Item Name"
                                                        value={item.name || ''}
                                                        onChange={(e) => handleItemsChange(index, 'name', e.target.value)}
                                                    />
                                                    <input 
                                                        type="text"
                                                        className="form-control me-2"
                                                        placeholder="Item Link"
                                                        value={item.link || ''}
                                                        onChange={(e) => handleItemsChange(index, 'link', e.target.value)}
                                                    />
                                                    <button 
                                                        className="btn btn-danger"
                                                        onClick={() => removeItem(index)}
                                                    >
                                                        <X size={18} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button 
                                                className="btn btn-secondary mt-2"
                                                onClick={addNewItem}
                                            >
                                                Add Item
                                            </button>
                                        </div>

                                        <div className="edit-actions">
                                            <button 
                                                className="btn btn-primary me-2"
                                                onClick={handleSavePage}
                                            >
                                                <Save size={18} className="me-1" /> Save
                                            </button>
                                            <button 
                                                className="btn btn-secondary"
                                                onClick={handleCancelEdit}
                                            >
                                                <X size={18} className="me-1" /> Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    // View Mode
                                    <div className="card-view-mode">
                                        <div className="edit-icon" onClick={() => handleEditPage(page)}>
                                            <Edit size={20} />
                                        </div>
                                        <div className="card-body text-center">
                                            {page.cardsinfo && page.cardsinfo.icon && (
                                                React.createElement(iconMap[page.cardsinfo.icon], { size: 24 })
                                            )}
                                            <h5 className="card-title">
                                                {page.cardsinfo?.title || 'Untitled'}
                                            </h5>
                                            <div className="cardsinfo-container mt-3">
                                                {page.cardsinfo?.items && page.cardsinfo.items.map((item, index) => (
                                                    <span
                                                        key={index}
                                                        className="cardsinfo-item mb-2 cursor-pointer"
                                                    >
                                                        <span className="cardsinfo-title" style={{color:"#718096"}}>
                                                            {item.name || 'Unnamed Item'}
                                                        </span>
                                                        {index < page.cardsinfo.items.length - 1 && (
                                                            <span className="separator">
                                                                <span className='cardsinfo-title'>|</span>
                                                            </span>
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ServicesEditPage;

